// @ts-nocheck
import React, { useRef, ComponentPropsWithoutRef, useEffect, useState } from 'react';
import classnames from 'classnames';
import IconAccordionCaretDown from '@lmig/lmds-react-icons/dist/icons/AccordionCaretDown';
import { Transition } from 'react-transition-group';
import { uniqueId } from '@lmig/lmds-helpers/dist/uniqueId';
import { useAccordionContext } from './hooks/useAccordionContext';
import { HeadingAsOptions } from './types';

export interface AccordionTabProps extends ComponentPropsWithoutRef<'button'> {
  /** Add an optional ID to this element: `<div class="lmig-Accordion-panel">`. If none is provided, a unique ID will be generated. */
  id?: string;

  /** Add custom classes to this element: `<div class="lmig-Accordion-panel">`. */
  className?: string;

  /** Set the visual label. */
  labelVisual: React.ReactNode;

  /** Display an icon to the left of the visual label. */
  icon?: React.ReactElement;

  /** Set the accordion's expansion state. If 'true', the accordion is expanded. */
  active?: boolean;

  /** Pass additional functionality for accordion button to run on click events. */
  accordionButtonOnClick?: (event?: React.MouseEvent<HTMLButtonElement>, index?: number) => void;

  /** Pass additional functionality for heading to run on click events. */
  onClick?: (event?: React.MouseEvent<HTMLButtonElement>, index?: number) => void;

  /** Pass additional functionality to run on blur events. */
  onBlur?: (event?: React.FocusEvent<HTMLButtonElement>, index?: number) => void;

  /** Pass additional functionality to run on focus events. */
  onFocus?: (event?: React.FocusEvent<HTMLButtonElement>, index?: number) => void;

  /** Set a reference to the DOM element. */
  innerRef?: React.RefObject<HTMLButtonElement>;

  /** Set the content of the accordion. Children are rendered in this element: `<div class="lmig-Accordion-content">`. */
  children: React.ReactNode;
  /** Specifies the HTML heading tag that to be used inside each tab control. */
  as?: HeadingAsOptions;

  /** Outgoing link from heading. */
  href?: string;
}

/**
 * @see [LMDS Accordion documentation and examples](https://designsystem.lmig.com/components/accordions/code)
 *
 * API:
 *
 * @implements [AccordionTabProps](https://designsystem.lmig.com/components/accordions/code#accordion-tab-props)
 * @extends [HTMLElement](https://microsoft.github.io/PowerBI-JavaScript/interfaces/_node_modules_typedoc_node_modules_typescript_lib_lib_dom_d_.htmlelement.html)
 */
export const AccordionTab = (props: AccordionTabProps): React.ReactElement => {
  const {
    id,
    labelVisual,
    icon,
    active,
    innerRef,
    className,
    onBlur,
    onFocus,
    accordionButtonOnClick,
    children,
    href,
    onClick,
    ...rest
  } = props;
  const { headingAs, accordionId, setActiveState, activeTabIDs, compact, collapsible } = useAccordionContext();

  const Heading = headingAs;
  const panelRef = useRef(null);
  // remove the `lmig-` from the accordionId if it exists to avoid duplication: lmig-lmig-accordionX-tabX
  // changed to useState because I found the id would change on each toggle of any AccordionTab within the Accordion.
  const [controlId] = useState(() => id || uniqueId(`${accordionId}-tab`.replace('lmig-', '')));

  // if the active prop changes, let the parent Accordion know to handle any logic for singleTab, etc.
  useEffect(() => {
    if (typeof active !== 'undefined') {
      setActiveState({ elementID: controlId, newTabState: active });
    }
  }, [active]);

  // local state checking against the tab array of ids
  const activeState = activeTabIDs.includes(controlId);

  const scaledIcon = (iconEl: React.ReactElement): React.ReactElement => {
    const sizeObj = compact ? { base: '16', sm: '24' } : { base: '24', sm: '32' };
    return React.cloneElement(iconEl, { size: sizeObj });
  };

  // this function is called if the consumer has an accordionButtonOnClick, onBlur, or onFocus and needs the index of the child
  // this keeps the AccordionTab backwards compatible
  const getChildIndex = (event: React.FocusEvent | React.MouseEvent): number => {
    const button = event.currentTarget as HTMLButtonElement;
    const listItem = button.closest('.lmig-Accordion-list-listItem');
    const { parentNode } = listItem;
    return Array.from(parentNode.children).indexOf(listItem);
  };

  const isAriaDisabled = !!(activeTabIDs.length === 1 && !collapsible && activeState);

  return (
    <li
      key={controlId}
      className={classnames('lmig-Accordion-list-listItem', {
        'lmig-Accordion--isOpen': activeState,
        'lmig-Accordion--hasIcon': icon,
      })}
    >
      <Heading className="lmig-Accordion-list-listItem-header">
        <button
          {...rest}
          className="lmig-Accordion-list-listItem-header-button"
          aria-controls={`${controlId}-panel`}
          aria-expanded={activeState}
          aria-disabled={isAriaDisabled}
          id={controlId}
          onClick={(e): void => {
            if (isAriaDisabled) return;
            // fix for both active and accordionButtonOnClick prop present
            const newTabState = typeof active !== 'undefined' ? active : !activeState;
            setActiveState({ elementID: controlId, newTabState });
            if (accordionButtonOnClick) {
              accordionButtonOnClick(e, getChildIndex(e));
            }
          }}
          {...(onBlur && {
            onBlur: (e): void => {
              onBlur(e, getChildIndex(e));
            },
          })}
          {...(onFocus && {
            onFocus: (e): void => {
              onFocus(e, getChildIndex(e));
            },
          })}
          ref={innerRef}
          type="button"
        >
          {icon && <span className="lmig-Accordion-list-listItem-header-icon">{scaledIcon(icon)}</span>}
          <a
            href={href}
            id={`${controlId}-label`}
            className="lmig-Accordion-list-listItem-header-button-labelVisual"
            onClick={onClick}
          >
            {labelVisual}
          </a>
          <a className="lmig-Accordion-list-listItem-header-caret">
            <IconAccordionCaretDown
              size="16"
              title={null}
              ariaHidden
              color="teal"
              className="lmig-Accordion-list-listItem-header-caret-svg"
            />
          </a>
        </button>
      </Heading>
      <Transition
        in={activeState}
        timeout={200}
        nodeRef={panelRef}
        onEnter={(): void => {
          panelRef.current.style.height = '0';
        }}
        onEntering={(): void => {
          panelRef.current.style.height = `${panelRef.current.scrollHeight}px`;
        }}
        onEntered={(): void => {
          panelRef.current.style.height = null;
        }}
        onExit={(): void => {
          panelRef.current.style.height = `${panelRef.current.scrollHeight}px`;
          // Reading a dimension property forces the browser to repaint, which allows the animation to work.
          panelRef.current.scrollHeight; // eslint-disable-line @typescript-eslint/no-unused-expressions
        }}
        onExiting={(): void => {
          panelRef.current.style.height = '0';
        }}
      >
        {(state): React.ReactElement => (
          <div
            aria-labelledby={`${controlId}-label`}
            className={classnames(className, 'lmig-Accordion-panel', {
              [`lmig-Accordion-panel--${state}`]: state,
            })}
            hidden={state === 'exited'}
            id={`${controlId}-panel`}
            role="region"
            ref={panelRef}
          >
            <div className="lmig-Accordion-content">{children}</div>
          </div>
        )}
      </Transition>
    </li>
  );
};
